import React, { ReactNode } from 'react';

import Badge, { TBadgeTone } from 'components/core/Badge';
import Button from 'components/core/Button';
import Card from 'components/core/Card';
import DropdownMenu, { TDropdownItem } from 'components/core/DropdownMenu';
import Switch from 'components/core/Switch';

import { isPublishedStatus, TGroupSummary, TGroupWireStatus } from 'types/story';

import {
  metaLines,
  pendingSummary,
  publishAffordance,
  publishedAgo,
} from '../../GroupsList.helpers';
import {
  StyledActionButtons,
  StyledActions,
  StyledCaption,
  StyledCover,
  StyledInfo,
  StyledMenu,
  StyledMeta,
  StyledPinnedDot,
  StyledStateBlock,
  StyledStateRow,
  StyledSwitchRow,
  StyledTitle,
  StyledTitleText,
  StyledTop,
} from './GroupCard.styles';

const BADGE_TONE: Record<TGroupWireStatus, TBadgeTone> = {
  DRAFT: 'neutral',
  LIVE: 'brand',
  SCHEDULED: 'info',
  ENDED: 'warning',
  DISABLED: 'danger',
};

const BADGE_LABEL: Record<TGroupWireStatus, string> = {
  DRAFT: 'Draft',
  LIVE: 'Live',
  SCHEDULED: 'Scheduled',
  ENDED: 'Ended',
  DISABLED: 'Disabled',
};

type TGroupCardProps = {
  group: TGroupSummary;
  onEditSlides: () => void;
  onEditDetails: () => void;
  onPublish: () => void;
  onToggleActive: (active: boolean) => void;
  onDelete: () => void;
};

const GroupCard = ({
  group,
  onEditSlides,
  onEditDetails,
  onPublish,
  onToggleActive,
  onDelete,
}: TGroupCardProps) => {
  const isPublished = isPublishedStatus(group.status);
  const publish = publishAffordance(group);

  const servingMeta = group.liveMeta ?? group;
  const publishedLabel = publishedAgo(group.publishedAt);

  const menuItems: TDropdownItem[] = [];
  if (isPublished) {
    menuItems.push({ label: 'Republish anyway', onClick: onPublish });
  } else {
    menuItems.push({ label: 'Delete', onClick: onDelete, danger: true });
  }

  let pendingLine: ReactNode = null;
  if (group.pending) {
    pendingLine = (
      <StyledCaption $warning>Waiting to publish: {pendingSummary(group.pending)}</StyledCaption>
    );
  } else if (group.hasLive) {
    pendingLine = <StyledCaption>No unpublished changes</StyledCaption>;
  }

  return (
    <Card>
      <StyledTop>
        <StyledCover $image={group.coverMediaUrl ?? undefined}>
          {group.coverMediaUrl ? '' : group.title.charAt(0) || '?'}
        </StyledCover>
        <StyledInfo>
          <StyledStateRow>
            <Badge tone={BADGE_TONE[group.status]} variant="soft" size="sm">
              {BADGE_LABEL[group.status]}
            </Badge>
            {group.pending ? (
              <Badge tone="warning" variant="solid" size="sm">
                Draft changes
              </Badge>
            ) : null}
          </StyledStateRow>
          <StyledTitle>
            <StyledTitleText>{group.title || group.name || 'Untitled group'}</StyledTitleText>
            {group.pinned ? <StyledPinnedDot>★</StyledPinnedDot> : null}
          </StyledTitle>
        </StyledInfo>
        {menuItems.length ? (
          <StyledMenu>
            <DropdownMenu items={menuItems} ariaLabel="More actions" />
          </StyledMenu>
        ) : null}
      </StyledTop>

      <StyledStateBlock>
        {metaLines(servingMeta).map((line) => (
          <StyledMeta key={line}>{line}</StyledMeta>
        ))}
        <StyledCaption>
          {publishedLabel ? `Published ${publishedLabel}` : 'Never published'}
        </StyledCaption>
        {pendingLine}
        {publish.tone === 'warning' && publish.reason ? (
          <StyledCaption $warning>{publish.reason}</StyledCaption>
        ) : null}
      </StyledStateBlock>

      <StyledActions>
        <StyledActionButtons>
          <Button variant="secondary" size="sm" onClick={onEditSlides}>
            Edit slides
          </Button>
          <Button variant="ghost" size="sm" onClick={onEditDetails}>
            Details
          </Button>
          <Button variant="primary" size="sm" disabled={!publish.isEnabled} onClick={onPublish}>
            {publish.label}
          </Button>
        </StyledActionButtons>
        <StyledSwitchRow>
          <Switch
            checked={isPublished}
            disabled={!group.hasLive}
            label={isPublished ? 'Active' : 'Disabled'}
            onChange={onToggleActive}
          />
        </StyledSwitchRow>
      </StyledActions>
    </Card>
  );
};

export default GroupCard;
